import { motion } from "framer-motion";
import { Star, ChevronLeft, ChevronRight } from "lucide-react";
import { useState, useEffect, useCallback, useMemo } from "react";
import { usePlatformConfig } from "@/hooks/usePlatformConfig";
import { useIsMobile } from "@/hooks/use-mobile";

const SocialProofSection = () => {
  const { config } = usePlatformConfig();
  const isMobile = useIsMobile();
  const testimonials = useMemo(() => config.testimonials.filter(t => t.is_active), [config.testimonials]);
  const [current, setCurrent] = useState(0);

  const itemsPerPage = isMobile ? 1 : 3;
  const totalPages = Math.max(1, Math.ceil(testimonials.length / itemsPerPage));

  const next = useCallback(() => setCurrent(c => (c + 1) % totalPages), [totalPages]);
  const prev = useCallback(() => setCurrent(c => (c - 1 + totalPages) % totalPages), [totalPages]);

  useEffect(() => {
    if (totalPages <= 1) return;
    const timer = setInterval(next, 5000);
    return () => clearInterval(timer);
  }, [next, totalPages]);

  // Reset current if out of bounds after resize
  useEffect(() => {
    if (current >= totalPages) setCurrent(0);
  }, [current, totalPages]);

  const visibleTestimonials = testimonials.slice(current * itemsPerPage, current * itemsPerPage + itemsPerPage);

  if (testimonials.length === 0) return null;

  return (
    <section className="py-20 border-t border-border">
      <div className="container">
        <motion.div
          className="text-center mb-14"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <span className="text-primary text-sm font-semibold uppercase tracking-wider">Depoimentos</span>
          <h2 className="text-3xl md:text-5xl font-bold mt-3 mb-4">
            Quem usa, <span className="text-gradient">recomenda</span>
          </h2>
          <p className="text-muted-foreground text-lg max-w-xl mx-auto">
            Veja o que nossos clientes dizem sobre a plataforma.
          </p>
        </motion.div>

        <div className="relative max-w-5xl mx-auto">
          <div className="grid md:grid-cols-3 gap-6">
            {visibleTestimonials.map((t, i) => (
              <motion.div
                key={t.id}
                className="rounded-2xl p-6 md:p-8 border border-border bg-card relative overflow-hidden group hover:border-primary/30 transition-colors"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
              >
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-primary opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="flex gap-1 mb-4">
                  {Array.from({ length: t.rating }).map((_, j) => (
                    <Star key={j} className="h-4 w-4 fill-primary text-primary" />
                  ))}
                </div>
                <p className="text-foreground leading-relaxed mb-6 text-sm md:text-base">"{t.text}"</p>
                <div className="flex items-center gap-3">
                  {t.avatar_url && (
                    <img src={t.avatar_url} alt={t.name} className="w-10 h-10 rounded-full object-cover" />
                  )}
                  <div>
                    <div className="font-semibold text-sm">{t.name}</div>
                    <div className="text-xs text-muted-foreground">{t.role}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-3 mt-8">
              <button onClick={prev} className="p-2 rounded-full border border-border hover:bg-secondary transition-colors">
                <ChevronLeft className="h-4 w-4" />
              </button>
              {Array.from({ length: totalPages }).map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrent(i)}
                  className={`w-2.5 h-2.5 rounded-full transition-all ${i === current ? 'bg-primary scale-125' : 'bg-muted-foreground/30'}`}
                />
              ))}
              <button onClick={next} className="p-2 rounded-full border border-border hover:bg-secondary transition-colors">
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default SocialProofSection;
